import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Paper
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';

function ServiceWiseBillingSummaryList(props) {
  const storedFilters = loadBillingFilters();

  const [academicYearList, setAcademicYearList] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all')
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('')
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const [sortModel, setSortModel] = React.useState([
    { field: 'balance_to_be_paid_by_today', sort: 'desc' }
  ])

  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = React.useState(0)
  const [overallSummaryData, setOverallSummaryData] = React.useState(null)
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource())

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const overallSummary = React.useMemo(() => {
    if (!overallSummaryData) {
      return {
        totals: { order: 0, issued: 0, invoiced: 0, paid: 0, balance: 0, discount: 0, dueByToday: 0, balanceToPayByToday: 0 }
      }
    }
    return overallSummaryData
  }, [overallSummaryData])

  React.useEffect(() => {
    getAcademicYearList()
  }, [])

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data.data || []
        setAcademicYearList(list)

        if (selectedAcademicYear) {
          fetchSummary(selectedAcademicYear)
        }
      }
    })
  }

  const fetchSummary = (yearId) => {
    if (!yearId) return

    setLoading(true)

    const params = {
      year: yearId,
      limit: pageSize,
      pageno: page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      order_source: orderSource,
      ...(sortModel?.length && {
        ordering: sortModel[0].sort === 'desc'
          ? `-${sortModel[0].field}`
          : sortModel[0].field
      })
    }

    getRequest(GET_URL.billing_config.api, { ...params, service_wise_finance_summary: 1 }, props)
      .then((res) => {
        setLoading(false)
        if (res?.status === 200) {
          setRows(res.data?.data || [])
          setRowCount(res.data?.count || 0)
          setOverallSummaryData(res.data?.summary || null)
        }
      })
      .catch(() => setLoading(false))
  }

  React.useEffect(() => {
    setPage(paginationModel.page)
    setPageSize(paginationModel.pageSize)
  }, [paginationModel])

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchSummary(selectedAcademicYear)
    }
  }, [statusByTeamFilter, clientBillingStatusFilter, sortModel, page, pageSize, selectedAcademicYear, orderSource])

  const columns = [
    {
      field: 'service_name',
      headerName: 'Service',
      width: 220,
      renderCell: (params) => {
        const balance = parseFloat(params.row.balance_amount || 0)
        const due = parseFloat(params.row.balance_to_be_paid_by_today || 0)

        let color = 'inherit'
        if (due > 0) color = '#d32f2f'
        else if (balance <= 0) color = '#2e7d32'

        return (
          <Typography fontWeight={600} color={color}>
            {params.value || '-'}
          </Typography>
        )
      }
    },
    { field: 'order_total_amount', headerName: 'Order Total', width: 140 },
    { field: 'issued_invoice_total_amount', headerName: 'Issued Invoice', width: 160 },
    { field: 'paid_total_amount', headerName: 'Paid', width: 130 },
    { field: 'balance_amount', headerName: 'Balance', width: 130 },
    { field: 'balance_to_be_paid_by_today', headerName: 'Due Today', width: 160 },
    { field: 'no_of_companies', headerName: 'Companies', width: 140 },
  ]

  return (
    <Box p={2}>

      {/* HEADER (NO PAPER) */}
      <Box mb={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700}>
            Service Wise Billing Summary
          </Typography>
          <OrdersSourceToggle
            value={orderSource}
            onChange={(next) => {
              setOrderSource(next);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          />
        </Box>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* FILTERS */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value)
                  saveBillingFilters({ academicYearId: e.target.value })
                  setPaginationModel((prev) => ({ ...prev, page: 0 }))
                }}
                label="Academic Year"
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Client Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Client Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value)
                  setPaginationModel((prev) => ({ ...prev, page: 0 }))
                }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={clientBillingStatusFilter}
                onChange={(e) => setClientBillingStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GOOD">GOOD</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY */}
      {selectedAcademicYear && rows.length > 0 && (
        <Box
          mb={2}
          p={2}
          borderRadius={2}
          sx={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
        >
          <Typography fontWeight={600} mb={1}>Overall Summary</Typography>
          <Divider sx={{ mb: 1 }} />

          <Grid container spacing={2}>
            <Grid item md={3}>Order: {formatAmount(overallSummary.totals.order)}</Grid>
            <Grid item md={3}>Paid: {formatAmount(overallSummary.totals.paid)}</Grid>
            <Grid item md={3}>Balance: {formatAmount(overallSummary.totals.balance)}</Grid>
            <Grid item md={3}>Due Today: {formatAmount(overallSummary.totals.balanceToPayByToday)}</Grid>
          </Grid>
        </Box>
      )}

      {/* TABLE */}
      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => String(row.id || row.service_id || row.service_name)}
          loading={loading}
          autoHeight
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f7fb',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9fbff'
            }
          }}
        />
      </Box>

    </Box>
  )
}

export default ServiceWiseBillingSummaryList;